import {animationFrames, Observable, takeUntil} from "rxjs";

/**
 * Keeps the visible part of the tasklist in place while content above it changes — rows inserted,
 * removed, or growing as they load their content.
 *
 * The browser's own scroll anchoring is switched off for the list (`overflow-anchor: none`): it does
 * not act at `scrollTop = 0`, and not when the topmost, partly hidden card grows (measured: the card
 * below moved 111–113 px), and any compensation running beside it needs a reference point that
 * anchoring would shift unnoticed. This class owns all compensation instead.
 *
 * How: once per frame it records, for every row near the viewport, its top edge in CONTENT
 * coordinates (`offsetTop`, independent of scrolling) together with `scrollTop`. When content changes,
 * the reference row is chosen on that recorded geometry — the first recorded row that would be fully
 * visible at the CURRENT `scrollTop` had nothing changed — and `scrollTop` is moved by the change of
 * that row's `offsetTop`. User scrolling never enters the difference. The record is refreshed after
 * every compensation, so a second compensation of the same change finds nothing to do.
 *
 * Measured 2026-10-01 on real cards: at most 0.5 px deviation while scrolling by wheel and by touch
 * fling, when inserting at `scrollTop = 0` followed by +1 564 px growth, and while the cards above
 * load; the earlier "reference card in viewport coordinates" design lost the whole growth (up to
 * 1 659 px) whenever growth and a scroll fell into the same frame.
 */
export class TasklistScrollKeeper {

    /** Rows within this distance (px) beyond the viewport are recorded, at least one fling's travel. */
    private static readonly RECORD_BAND = 1000;

    private _record = new Map<HTMLElement, number>();

    private _suspended = false;

    /** A row kept in place in preference to the default reference (see pin()). */
    private _pinned: HTMLElement | null = null;

    private readonly _resizeObserver: ResizeObserver;

    private readonly _mutationObserver: MutationObserver;

    public constructor(
        private readonly _container: HTMLElement,
        private readonly _isRow: (element: Element) => boolean,
    ) {
        // Growth of any row in the block (not only visible ones: a row may start loading in the band
        // and finish after it has scrolled out). Runs synchronously in the callback — deferring it
        // would paint one frame with the shift visible.
        this._resizeObserver = new ResizeObserver(() => this.compensate());
        this._mutationObserver = new MutationObserver(records => {
            for (const record of records) {
                record.addedNodes.forEach(node => {
                    if (node instanceof HTMLElement && this._isRow(node)) {
                        this._resizeObserver.observe(node);
                    }
                });
            }
        });
        this._mutationObserver.observe(this._container, {childList: true});
        Array.from(this._container.children).filter(this._isRow).forEach(row => this._resizeObserver.observe(row));
    }

    /**
     * Runs compensate-then-record once per animation frame until `until$` emits. Compensating BEFORE
     * recording matters: a row that grew inside a scroll handler would otherwise already be recorded
     * grown, and the later compensation would find a difference of 0.
     */
    public start(until$: Observable<unknown>): void {
        animationFrames().pipe(takeUntil(until$)).subscribe(() => {
            this.compensate();
            this.record();
        });
        this.record();
    }

    /** Stops compensating (during a restore, which sets one absolute position itself). */
    public suspend(): void {
        this._suspended = true;
    }

    /** Records the current geometry and resumes compensating. */
    public resume(): void {
        this.record();
        this._suspended = false;
    }

    /**
     * Keeps `row` in place instead of the default reference (the first fully visible row) — after a
     * jump, the null point. Without it, cards loading above the null point but inside the view push
     * it down; for a null point in the last screen that pushed it out of view (measured 2026-10-01:
     * 435 px below the visible area). Unpinned as soon as the user scrolls: a pinned row that has
     * scrolled out of view would otherwise make changes between it and the view move the view.
     */
    public pin(row: HTMLElement | null): void {
        this._pinned = row;
    }

    /** Records the content-coordinate top edge of every row near the viewport. */
    public record(): void {
        const viewTop = this._container.scrollTop;
        const viewBottom = viewTop + this._container.clientHeight;
        const record = new Map<HTMLElement, number>();
        for (const row of this._rows()) {
            const top = row.offsetTop;
            const bottom = top + row.offsetHeight;
            if (row === this._pinned || (bottom > viewTop - TasklistScrollKeeper.RECORD_BAND && top < viewBottom + TasklistScrollKeeper.RECORD_BAND)) {
                record.set(row, top);
            }
        }
        this._record = record;
    }

    /**
     * Moves `scrollTop` by the change of the reference row's content position since the last record,
     * then records again. Returns the applied difference (0 when nothing moved or suspended).
     */
    public compensate(): number {
        if (this._suspended) {
            return 0;
        }

        const scrollTop = this._container.scrollTop;
        const viewHeight = this._container.clientHeight;
        let difference = 0;
        const pinned = this._pinned;
        if (pinned !== null && pinned.isConnected && this._record.has(pinned)) {
            difference = pinned.offsetTop - this._record.get(pinned)!;
            if (Math.abs(difference) > 0.01) {
                this._container.scrollTop = scrollTop + difference;
            }
            this.record();
            return difference;
        }
        for (const [row, recordedTop] of this._record) {
            if (!row.isConnected) {
                continue;
            }
            const topInView = recordedTop - scrollTop;
            if (topInView >= -0.5 && topInView < viewHeight) {
                difference = row.offsetTop - recordedTop;
                break;
            }
        }

        if (Math.abs(difference) > 0.01) {
            this._container.scrollTop = scrollTop + difference;
        }
        this.record();

        return difference;
    }

    /** Stops observing. */
    public dispose(): void {
        this._resizeObserver.disconnect();
        this._mutationObserver.disconnect();
        this._record.clear();
    }

    private _rows(): HTMLElement[] {
        return (Array.from(this._container.children) as HTMLElement[]).filter(this._isRow);
    }
}
